import { X, Mail } from 'lucide-react';
import { GALLERY_TEMPLATES, type GalleryTemplate } from '@/lib/emailBlocks';

interface Props {
  onSelect: (template: GalleryTemplate) => void;
  onBlank: () => void;
  onClose: () => void;
}

export function TemplateGallery({ onSelect, onBlank, onClose }: Props) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-4xl mx-4 bg-white rounded-2xl shadow-2xl max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Choose a Template</h2>
            <p className="text-sm text-slate-500">Pick a pre-made template or start from scratch</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>

        {/* Gallery Grid */}
        <div className="p-6 overflow-y-auto max-h-[65vh]">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Blank Template */}
            <button onClick={onBlank}
              className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-[#6366F1] hover:bg-indigo-50/30 transition-colors group">
              <div className="h-10 w-10 rounded-full bg-slate-100 group-hover:bg-indigo-100 flex items-center justify-center mx-auto mb-3">
                <Mail className="h-5 w-5 text-slate-400 group-hover:text-[#6366F1]" />
              </div>
              <p className="text-sm font-semibold text-slate-700">Blank Template</p>
              <p className="text-xs text-slate-400 mt-1">Start from scratch</p>
            </button>

            {/* Gallery Templates */}
            {GALLERY_TEMPLATES.map(t => (
              <button key={t.category} onClick={() => onSelect(t)}
                className="border border-slate-200 rounded-xl overflow-hidden text-left hover:shadow-lg hover:border-transparent transition-all group">
                {/* Color preview header */}
                <div className="h-20 flex items-center justify-center" style={{ backgroundColor: t.color }}>
                  <span className="text-white text-sm font-bold">{t.blocks[0]?.props?.text || t.name}</span>
                </div>
                <div className="p-3">
                  <p className="text-sm font-semibold text-slate-800">{t.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{t.description}</p>
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">{t.blocks.length} blocks</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
